import { Card } from '@/components/ui/card';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { useFormContext } from 'react-hook-form';
import type { z } from 'zod';
import { useGroupPricingCalculations } from '../hooks/use-group-pricing-calculations';
import type { FormSchema } from '../zod';

interface CalculationsSummaryProps {
	className?: string;
}

export function CalculationsSummary({
	className = '',
}: CalculationsSummaryProps) {
	const form = useFormContext<z.infer<typeof FormSchema>>();
	const calculations = useGroupPricingCalculations(form);

	const formatCurrency = (value: number) => {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
		}).format(value);
	};

	const formatNumber = (value: number) => {
		return new Intl.NumberFormat('en-US').format(value);
	};

	return (
		<Card className={`tw-p-4 ${className}`}>
			<h3 className='tw-text-lg tw-font-semibold tw-mb-2'>Group Summary</h3>

			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Metric</TableHead>
						<TableHead className='tw-text-right'>Value</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					<TableRow>
						<TableCell>Nights</TableCell>
						<TableCell className='tw-text-right'>
							{calculations.isValidDateRange
								? calculations.numberOfNights
								: '—'}
						</TableCell>
					</TableRow>
					<TableRow>
						<TableCell>Total Room Nights</TableCell>
						<TableCell className='tw-text-right'>
							{calculations.totalRoomNights > 0
								? formatNumber(calculations.totalRoomNights)
								: '—'}
						</TableCell>
					</TableRow>
					<TableRow>
						<TableCell>Total Revenue</TableCell>
						<TableCell className='tw-text-right'>
							{calculations.totalRevenue > 0
								? formatCurrency(calculations.totalRevenue)
								: '—'}
						</TableCell>
					</TableRow>
					<TableRow>
						<TableCell>Net Revenue (after commission)</TableCell>
						<TableCell className='tw-text-right'>
							{calculations.netRevenue > 0
								? formatCurrency(calculations.netRevenue)
								: '—'}
						</TableCell>
					</TableRow>
					<TableRow>
						<TableCell>Profit per Room</TableCell>
						<TableCell className='tw-text-right'>
							{calculations.profitPerRoom > 0
								? formatCurrency(calculations.profitPerRoom)
								: '—'}
						</TableCell>
					</TableRow>
				</TableBody>
			</Table>
		</Card>
	);
}
